import { ContextMenuAction } from '@dtx-company/shared-components/src'
import { FolderAncestor } from './Folders.types'
import { GetAncestors } from '../myflowcodes/CreateFolderV2/CreateFolderV2.hooks'
import { RefObject, useMemo, useRef, useState } from 'react'
import useOnClickOutside from '@dtx-company/true-common/src/hooks/useOnClickOutside'

interface FolderBreadcrumbsState {
  displayHiddenFolders: boolean
  hiddenFolderActions: ContextMenuAction[]
  visibleFolders: FolderAncestor[]
  hiddenFolders: FolderAncestor[]
  hiddenFolderMenuRef: RefObject<HTMLDivElement>
  toggleHiddenFolders: () => void
}

export const useFolderBreadcrumbsState = ({
  ancestors,
  maxLength,
  enableBreadcrumbNavigation,
  handleBreadcrumbClick
}: {
  ancestors: GetAncestors['ancestors']
  maxLength: number
  enableBreadcrumbNavigation: boolean
  handleBreadcrumbClick?: (id: string, isRootfolder?: boolean) => void
}): FolderBreadcrumbsState => {
  const [displayHiddenFolders, setDisplayHiddenFolders] = useState(false)
  const hiddenFolderMenuRef = useRef<HTMLInputElement>(null)

  useOnClickOutside(hiddenFolderMenuRef, () => {
    setDisplayHiddenFolders(false)
  })

  const { visibleFolders, hiddenFolders } = useMemo(() => {
    const totalAncestors = [...ancestors]
    const visibleFolders = totalAncestors.splice(-maxLength)
    return {
      visibleFolders,
      hiddenFolders: totalAncestors
    }
  }, [ancestors, maxLength])

  const toggleHiddenFolders = (): void => {
    setDisplayHiddenFolders(prevState => !prevState)
  }

  const hiddenFolderActions: ContextMenuAction[] = hiddenFolders.map(hiddenFolder => ({
    id: hiddenFolder.id,
    label: hiddenFolder.name,
    img: {
      src: '/icons/folder-outlined-grey.svg',
      alt: ''
    },
    action: () => {
      toggleHiddenFolders()
      if (enableBreadcrumbNavigation) {
        handleBreadcrumbClick?.(hiddenFolder.id)
      }
    }
  }))

  return {
    displayHiddenFolders,
    hiddenFolderActions,
    visibleFolders,
    hiddenFolderMenuRef,
    toggleHiddenFolders,
    hiddenFolders
  }
}

export interface FormattedAncestors {
  parentFolderData?: {
    id?: string
    name?: string
    batchCount?: number
  }
  ancestors?: FolderAncestor[]
}

export const useFormattedAncestors = ({
  parentFolderData,
  ancestors
}: FormattedAncestors): FolderAncestor[] => {
  return useMemo(() => {
    const parentFolder: FolderAncestor | undefined = parentFolderData?.id
      ? {
          id: parentFolderData?.id ?? '',
          name: parentFolderData?.name ?? '',
          batchCount: parentFolderData?.batchCount
        }
      : undefined

    return (parentFolder && ancestors?.concat(parentFolder)) || []
  }, [parentFolderData, ancestors])
}
